'use client';

import { c as _c } from "react/compiler-runtime";
import { type ItemsRenderProps, useGetMarketsInfinite, useMarketConfigCatalog } from '@morfi/backend-sdk-v2/client';
import { usePathname, useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import type { Dispatch, SetStateAction } from 'react';
import { useEffect, useRef, useState } from 'react';
import { GlobeButton } from '@/components/globe/globe-button';
import { MarketsHowItWorksModal } from '@/components/how-it-works/markets-how-it-works-modal';
import { marketTypeFilterParams, parseMarketTypeParam, useMarketTypeFilter } from '@/components/market/identity/market-identity-filter';
import { MarketFeedTradeProvider } from '@/components/markets/live-trades/market-feed-trade-provider';
import { MarketRowCardSkeleton } from '@/components/markets/market-row-card-skeleton';
import { type FilterDimension, type FilterValue, SortFilterBar } from '@/components/shared/sort-filter-bar';
import type { SortPillOption } from '@/components/shared/sort-pill-row';
import { AnimatedButton } from '@/components/ui/animated-button';
import type { ChipOption } from '@/components/ui/chip-multi-select';
import { Icon } from '@/components/ui/icon';
import { Award01IconSolid, Bookmark02Icon, Bookmark02IconSolid, CheckmarkCircle02IconSolid, Clock04IconSolid, FilterVerticalIcon, FireIconSolid, HelpCircleIcon, Loading03Icon, Plant04IconSolid, Search02Icon } from '@/components/ui/icon/glyphs';
import type { MultiSelectOption } from '@/components/ui/searchable-multi-select';
import { categoryNameToId, locationNameToId, PROPOSAL_CATEGORIES, PROPOSAL_LOCATIONS } from '@/config/proposal-options';
import { onEvent } from '@/events';
import { useAuthGate } from '@/hooks/use-auth-gate';
import { useAuthenticatedWatchlist } from '@/hooks/use-authenticated-watchlist';
import { useFeatureGate } from '@/hooks/use-feature-gate';
import { useRoutePerf } from '@/hooks/use-route-perf';
import { useFormattedCurrency } from '@/lib/currency';
import { creatorParam, csvParam, dateRangeParams, dateRangeToParam, dollarsToD6, intString, type OutcomesBounds, paramToCsv, paramToDateRange, paramToRange, type Range, rangeParams, rangeToParam } from '@/lib/filters/feed-filters';
import { MARKETS_LIST_OPEN_STATUS, MARKETS_LIST_RESOLVED_STATUS } from '@/lib/market/listing-status';
import { LISTING_INDEX_PAGE_STACK_CLASS } from '@/lib/ui/layout';
import { cn } from '@/lib/ui/utils';
import { useGraduationModalStore } from '@/stores/graduation-modal';
import { useLayoutStore } from '@/stores/layout';
import { useManipulationModalStore } from '@/stores/manipulation-modal';
import { useMarketsSortStore } from '@/stores/markets-sort';
import { EmptyState } from './empty-state';
import { type MarketsEmptyStateKind, resolveMarketsEmptyVariant } from './empty-state-variant';
import { MarketsFeaturedSection } from './featured-market-hero/markets-featured-section';
import { IntroWidget } from './intro-widget/intro-widget';
import { MarketRowCard } from './market-row-card';
import { MarketsFeedError } from './markets-feed-error';
import { type MarketsFeedPhase, resolveMarketsFeedPhase } from './markets-feed-phase';
import { getMarketsFeedSortOrder, MARKETS_FEED_DEFAULT_SORT, MARKETS_FEED_SORT_KEYS } from './markets-feed-sorts';
import { MarketsHeader } from './markets-header';
import { NoResults } from './no-results';
import { SparseCard } from './sparse-card';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SEARCH_DEBOUNCE_MS = 300;

// Stable keys for the in-page filter-change skeleton stripe. Three rows
// covers the most-common viewport widths (1/2/3 columns) without
// overflowing the visible region — the real grid lands within ~hundreds
// of ms once the new query settles, so a long stripe would mostly just
// flash.
const SKELETON_STRIPE_KEYS: readonly string[] = ['stripe-0', 'stripe-1', 'stripe-2'];
const DEFAULT_STATUS = 'trading';
const VALID_STATUSES: Set<string> = new Set(['trading', 'resolved']);

/**
 * Unsatisfiable-but-valid `pubkeys` filter for the empty-watchlist case. The
 * all-1s base58 string is the Solana system/default pubkey (32 zero bytes); it
 * decodes cleanly (unlike a `__none__`-style sentinel, which throws in the
 * backend's strict base58 decoder) and matches no real market, so the feed
 * stays empty instead of the backend's missing-filter branch returning every
 * market (MOR-386).
 */
const WATCHLIST_EMPTY_SENTINEL = '11111111111111111111111111111111';

/**
 * Pseudo-sort value used to render the "Winners" pill in the sort tab row.
 * It's not a real sort — clicking it switches the status filter to 'resolved'
 * (which the Winners feed reads as "show graduated markets") while leaving the
 * underlying sortBy untouched, so toggling back to a real sort restores the
 * user's previous sort selection.
 */
const RESOLVED_VIEW_VALUE = 'resolved-view';
const VALID_SORT_VALUES: Set<string> = new Set(MARKETS_FEED_SORT_KEYS);
const DEFAULT_SORT: string = MARKETS_FEED_DEFAULT_SORT;
const VALID_LOCATIONS: Set<string> = new Set(PROPOSAL_LOCATIONS.map(l => l.name));

/**
 * MOR-1800: the `?category=` param must apply regardless of casing or
 * id-vs-name form (`technology`, `Technology`, `TECH…`). Resolves to the
 * canonical display name when the value matches a known category id or name;
 * otherwise keeps the raw value so the backend's exact-match filter still
 * applies (live data carries legacy non-canonical slugs like `tech`).
 */
function resolveCategoryParam(value: string): string | null {
  const v = value.trim();
  if (v.length === 0) return null;
  const match = PROPOSAL_CATEGORIES.find(c => c.id.toLowerCase() === v.toLowerCase() || c.name.toLowerCase() === v.toLowerCase());
  return match?.name ?? v;
}
const REGION_MULTI_OPTIONS: MultiSelectOption[] = PROPOSAL_LOCATIONS.map(l => ({
  value: l.name,
  label: l.name,
  emoji: l.emoji
}));
const CATEGORY_CHIP_OPTIONS: ChipOption[] = PROPOSAL_CATEGORIES.map(c => ({
  value: c.name,
  label: c.name,
  emoji: c.emoji
}));

/** Read a FilterValue as a string[] (chipsMulti), defaulting to empty. */
function asStrArr(value: FilterValue | undefined): string[] {
  if (Array.isArray(value) && (value.length === 0 || typeof value[0] === 'string')) {
    return value as string[];
  }
  return [];
}

// Pre-resolution fallbacks for the outcomes bounds. The real values come from
// PlatformConfig.{minOutcomes,maxOutcomes}; these only apply before the config
// query resolves or if it fails.
const FALLBACK_MIN_OUTCOMES = 2;
const FALLBACK_MAX_OUTCOMES = 12;
// Slider/date bounds — mirror the filter-overhaul mockup (`/tmp/morfi-filters`).
// `outcomes` here is only the fallback span; the live bounds are sourced from
// PlatformConfig and threaded as `outcomesBounds`.
const M = {
  outcomes: {
    min: FALLBACK_MIN_OUTCOMES,
    max: FALLBACK_MAX_OUTCOMES
  },
  volume: {
    min: 0,
    max: 100_000
  },
  prizePool: {
    min: 0,
    max: 50_000
  },
  liquidity: {
    min: 0,
    max: 250_000
  },
  backers: {
    min: 0,
    max: 500
  },
  traders: {
    min: 0,
    max: 1000
  }
} as const;
const MARKETS_CREATED_SPAN_DAYS = 180;

/** Read a FilterValue as a MultiSelectOption[] (searchMulti / creatorMulti). */
function asOptArr(value: FilterValue | undefined): MultiSelectOption[] {
  if (Array.isArray(value) && (value.length === 0 || typeof value[0] === 'object')) {
    return value as MultiSelectOption[];
  }
  return [];
}

/** Read a FilterValue as a `[lo, hi]` range, or `undefined` when not a range. */
function asRangeVal(value: FilterValue | undefined): Range | undefined {
  if (Array.isArray(value) && value.length === 2 && typeof value[0] === 'number') {
    return [value[0], value[1] as number];
  }
  return undefined;
}
const MARKETS_FILTER_TYPE_BY_KEY: Record<string, 'category' | 'location' | 'outcomes' | 'volume' | 'prizePool' | 'liquidity' | 'backers' | 'traders' | 'createdAt' | 'creator'> = {
  category: 'category',
  location: 'location',
  outcomes: 'outcomes',
  volume: 'volume',
  prizePool: 'prizePool',
  liquidity: 'liquidity',
  backers: 'backers',
  traders: 'traders',
  createdAt: 'createdAt',
  creator: 'creator'
};

/** A compact analytics-safe summary of any FilterValue. */
function summariseFilterValue(value: FilterValue | undefined): string {
  if (value === undefined || value === null) return 'all';
  if (typeof value === 'string') return value.length > 0 ? value : 'all';
  if (Array.isArray(value)) {
    if (value.length === 0) return 'all';
    if (typeof value[0] === 'number') return `${value[0]}-${value[1] as number}`;
    if (typeof value[0] === 'string') return (value as string[]).join(',');
    return (value as MultiSelectOption[]).map(o => o.value).join(',');
  }
  return 'all';
}

/** A fresh, fully-cleared markets overhaul value map. */
function buildEmptyMarketsOverhaul(outcomesBounds: OutcomesBounds): Record<string, FilterValue | undefined> {
  return {
    location: [],
    outcomes: [outcomesBounds.min, outcomesBounds.max],
    volume: [M.volume.min, M.volume.max],
    prizePool: [M.prizePool.min, M.prizePool.max],
    liquidity: [M.liquidity.min, M.liquidity.max],
    backers: [M.backers.min, M.backers.max],
    traders: [M.traders.min, M.traders.max],
    createdAt: [0, 100],
    creator: []
  };
}

/** Serialise the markets overhaul values to a shareable URL param map. */
function buildMarketsUrlParams(overhaul: Record<string, FilterValue | undefined>, outcomesBounds: OutcomesBounds, regionEnabled: boolean, identityEnabled: boolean): Record<string, string> {
  // Category is intentionally NOT serialised here — the global top category nav
  // owns the `?category=` param. The overhaul URL key carries only modal filters.
  const out: Record<string, string> = marketTypeFilterParams(overhaul.marketType, identityEnabled);
  if (regionEnabled) {
    const regions = asOptArr(overhaul.location).map(o => o.value);
    if (regions.length > 0) out.location = regions.join(',');
  }
  const addRange = (key: string, range: Range | undefined, min: number, max: number): void => {
    if (range === undefined) return;
    const v = rangeToParam(range, min, max);
    if (v !== null) out[key] = v;
  };
  addRange('outcomes', asRangeVal(overhaul.outcomes), outcomesBounds.min, outcomesBounds.max);
  addRange('volume', asRangeVal(overhaul.volume), M.volume.min, M.volume.max);
  addRange('prizePool', asRangeVal(overhaul.prizePool), M.prizePool.min, M.prizePool.max);
  addRange('liquidity', asRangeVal(overhaul.liquidity), M.liquidity.min, M.liquidity.max);
  addRange('backers', asRangeVal(overhaul.backers), M.backers.min, M.backers.max);
  addRange('traders', asRangeVal(overhaul.traders), M.traders.min, M.traders.max);
  const created = asRangeVal(overhaul.createdAt);
  if (created !== undefined) {
    const v = dateRangeToParam(created);
    if (v !== null) out.createdAt = v;
  }
  return out;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Expand the virtual `trading`/`resolved` UI toggle to `/v1/markets` status tokens. */
function resolveStatusArg(status: string): string | undefined {
  if (status === 'resolved') return MARKETS_LIST_RESOLVED_STATUS;
  if (status === 'trading') return MARKETS_LIST_OPEN_STATUS;
  if (status === 'all') return undefined;
  return status;
}

/** Build a query string from non-default filter/sort values. */
function buildSearchString(sort: string, status: string, search: string, currentParams: URLSearchParams, overhaulUrlKey: string): string {
  const params = new URLSearchParams();
  if (sort !== DEFAULT_SORT) params.set('sort', sort);
  // Category is owned by the global top nav (`CategoryNav`) in BOTH modes — it
  // drives the feed + featured-hero visibility. Preserve it STRAIGHT FROM THE
  // URL (not the one-render-lagged `category` state) so this sync never fights
  // the category→state effect and never erases the nav selection.
  const cat = currentParams.get('category');
  if (cat !== null) params.set('category', cat);
  // The overhaul owns the gated location filter and range filters — emit from
  // its serialised query string.
  for (const [k, v] of new URLSearchParams(overhaulUrlKey)) params.set(k, v);
  if (status !== DEFAULT_STATUS) params.set('status', status);
  if (search.length > 0) params.set('q', search);
  const qs = params.toString();
  return qs.length > 0 ? `?${qs}` : '';
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface MarketsPageModel {
  t: ReturnType<typeof useTranslations>;
  tFilters: ReturnType<typeof useTranslations>;
  category: string | null;
  status: string;
  sortBy: string;
  showFeaturedHero: boolean;
  marketWatchlistPubkeys: readonly string[];
  feedPhase: MarketsFeedPhase;
  retryFeed: () => void;
  listIsLoading: boolean;
  totalMarkets: number;
  emptyStateVariant: MarketsEmptyStateKind;
  showCreateCta: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  ids: readonly string[];
  Items: ReturnType<typeof useGetMarketsInfinite>['Items'];
  /** First-seen index per market id — drives above-the-fold `priorityImage`. */
  marketRowIndexById: Map<string, number>;
  sortOptions: SortPillOption[];
  activeSortPill: string;
  handleSortPillChange: (next: string) => void;
  handleSortPillEvent: (value: string, previous: string) => void;
  filterDimensions: FilterDimension[];
  activeFilterValues: Record<string, FilterValue | undefined>;
  handleFilterChange: (key: string, value: FilterValue) => void;
  handleClearFilters: () => void;
  filterOpen: boolean;
  setFilterOpen: Dispatch<SetStateAction<boolean>>;
  helpModalOpen: boolean;
  setHelpModalOpen: Dispatch<SetStateAction<boolean>>;
  openGraduation: (open: boolean) => void;
  openManipulation: (open: boolean) => void;
  headerActions: React.ReactNode;
}

/** Own feed membership, URL filters and page controls; live rows belong to child subscribers. */
function useMarketsPage() {
  const $ = _c(365);
  const t = useTranslations("markets");
  const tFilters = useTranslations("filters");
  const regionEnabled = useFeatureGate("region") === true;
  const identityEnabled = useFeatureGate("creator_and_platform_markets") === true;
  const marketTypeFilter = useMarketTypeFilter(identityEnabled);
  const {
    exchangeRate,
    currencySymbol
  } = useFormattedCurrency();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const initialSort = searchParams.get("sort");
  const initialCategory = searchParams.get("category");
  const initialStatus = searchParams.get("status");
  const [sortBy, setSortBy] = useState(initialSort !== null && VALID_SORT_VALUES.has(initialSort) ? initialSort : DEFAULT_SORT);
  const [status, setStatus] = useState(initialStatus !== null && VALID_STATUSES.has(initialStatus) ? initialStatus : DEFAULT_STATUS);
  const [category, setCategory] = useState(initialCategory !== null ? resolveCategoryParam(initialCategory) : null);
  let t0;
  if ($[0] !== searchParams) {
    t0 = () => searchParams.get("q") ?? "";
    $[0] = searchParams;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const [search] = useState(t0);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const catalog = useMarketConfigCatalog();
  let t1;
  if ($[2] !== catalog.configs) {
    t1 = Object.values(catalog.configs ?? {});
    $[2] = catalog.configs;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const configs = t1;
  let t2;
  if ($[4] !== configs) {
    t2 = configs.length === 0 ? FALLBACK_MIN_OUTCOMES : Math.min(...configs.map(_temp));
    $[4] = configs;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== configs) {
    t3 = configs.length === 0 ? FALLBACK_MAX_OUTCOMES : Math.max(...configs.map(_temp2));
    $[6] = configs;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t2 || $[9] !== t3) {
    t4 = {
      min: t2,
      max: t3
    };
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const outcomesBounds = t4;
  let t5;
  if ($[11] !== outcomesBounds.max || $[12] !== outcomesBounds.min || $[13] !== searchParams) {
    t5 = () => ({
      marketType: parseMarketTypeParam(searchParams.get("marketType")),
      location: REGION_MULTI_OPTIONS.filter(o => paramToCsv(searchParams.get("location"), VALID_LOCATIONS).includes(o.value)),
      outcomes: paramToRange(searchParams.get("outcomes"), outcomesBounds.min, outcomesBounds.max),
      volume: paramToRange(searchParams.get("volume"), M.volume.min, M.volume.max),
      prizePool: paramToRange(searchParams.get("prizePool"), M.prizePool.min, M.prizePool.max),
      liquidity: paramToRange(searchParams.get("liquidity"), M.liquidity.min, M.liquidity.max),
      backers: paramToRange(searchParams.get("backers"), M.backers.min, M.backers.max),
      traders: paramToRange(searchParams.get("traders"), M.traders.min, M.traders.max),
      createdAt: paramToDateRange(searchParams.get("createdAt")),
      creator: []
    });
    $[11] = outcomesBounds.max;
    $[12] = outcomesBounds.min;
    $[13] = searchParams;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const [overhaul, setOverhaul] = useState(t5);
  let t6;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = function setOverhaulValue(key, value) {
      setOverhaul(prev => ({
        ...prev,
        [key]: value
      }));
    };
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const setOverhaulValue = t6;
  const debounceTimerRef = useRef(null);
  let t7;
  if ($[16] !== searchParams) {
    t7 = searchParams.get("category");
    $[16] = searchParams;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const categoryParam = t7;
  let t8;
  let t9;
  if ($[18] !== categoryParam) {
    t8 = () => {
      setCategory(categoryParam !== null ? resolveCategoryParam(categoryParam) : null);
    };
    t9 = [categoryParam];
    $[18] = categoryParam;
    $[19] = t8;
    $[20] = t9;
  } else {
    t8 = $[19];
    t9 = $[20];
  }
  useEffect(t8, t9);
  let t10;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      min: FALLBACK_MIN_OUTCOMES,
      max: FALLBACK_MAX_OUTCOMES
    };
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  const prevOutcomesBoundsRef = useRef(t10);
  let t11;
  let t12;
  if ($[22] !== outcomesBounds.max || $[23] !== outcomesBounds.min) {
    t11 = () => {
      const prev_0 = prevOutcomesBoundsRef.current;
      if (prev_0.min === outcomesBounds.min && prev_0.max === outcomesBounds.max) {
        return;
      }
      prevOutcomesBoundsRef.current = {
        min: outcomesBounds.min,
        max: outcomesBounds.max
      };
      setOverhaul(cur => {
        const range = asRangeVal(cur.outcomes);
        if (range !== undefined && range[0] === prev_0.min && range[1] === prev_0.max) {
          return {
            ...cur,
            outcomes: [outcomesBounds.min, outcomesBounds.max]
          };
        }
        return cur;
      });
    };
    t12 = [outcomesBounds.min, outcomesBounds.max];
    $[22] = outcomesBounds.max;
    $[23] = outcomesBounds.min;
    $[24] = t11;
    $[25] = t12;
  } else {
    t11 = $[24];
    t12 = $[25];
  }
  useEffect(t11, t12);
  const categorySelected = categoryParam !== null ? categoryParam.split(",").map(resolveCategoryParam).filter(_temp3) : [];
  let t13;
  if ($[26] !== pathname || $[27] !== searchParams) {
    t13 = function applyCategoryToUrl(values) {
      const params = new URLSearchParams(searchParams.toString());
      if (values.length > 0) {
        params.set("category", values.join(","));
      } else {
        params.delete("category");
      }
      const qs = params.toString();
      const nextUrl = `${pathname}${qs.length > 0 ? `?${qs}` : ""}`;
      window.history.replaceState(null, "", nextUrl);
    };
    $[26] = pathname;
    $[27] = searchParams;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  const applyCategoryToUrl = t13;
  let t14;
  if ($[29] !== searchParams) {
    t14 = () => searchParams.get("view") === "watchlist";
    $[29] = searchParams;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  const [watchlistActive, setWatchlistActive] = useState(t14);
  let t15;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = {
      kind: "markets",
      fields: ["pubkeys"]
    };
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  const {
    data: marketsWatchlist,
    isPending: marketsWatchlistPending
  } = useAuthenticatedWatchlist(t15);
  let t16;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = {
      kind: "winners",
      fields: ["pubkeys"]
    };
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  const t17 = status === "resolved";
  let t18;
  if ($[33] !== t17) {
    t18 = {
      enabled: t17
    };
    $[33] = t17;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  const {
    data: winnersWatchlist,
    isPending: winnersWatchlistPending
  } = useAuthenticatedWatchlist(t16, t18);
  const watchlistPending = status === "resolved" ? marketsWatchlistPending || winnersWatchlistPending : marketsWatchlistPending;
  const watchlistLoading = watchlistActive && watchlistPending;
  const [filterOpen, setFilterOpen] = useState(false);
  const [helpModalOpen, setHelpModalOpen] = useState(false);
  const openGraduation = useGraduationModalStore(_temp4);
  const openManipulation = useManipulationModalStore(_temp5);
  const {
    ensureAuthenticated
  } = useAuthGate();
  const searchModalOpen = useLayoutStore(_temp6);
  const setSearchOpen = useLayoutStore(_temp7);
  let t19;
  let t20;
  if ($[35] !== search) {
    t19 = () => {
      if (debounceTimerRef.current !== null) {
        clearTimeout(debounceTimerRef.current);
      }
      debounceTimerRef.current = setTimeout(() => {
        debounceTimerRef.current = null;
        setDebouncedSearch(search);
      }, SEARCH_DEBOUNCE_MS);
      return () => {
        if (debounceTimerRef.current !== null) {
          clearTimeout(debounceTimerRef.current);
        }
      };
    };
    t20 = [search];
    $[35] = search;
    $[36] = t19;
    $[37] = t20;
  } else {
    t19 = $[36];
    t20 = $[37];
  }
  useEffect(t19, t20);
  let t21;
  if ($[38] !== identityEnabled || $[39] !== outcomesBounds || $[40] !== overhaul || $[41] !== regionEnabled) {
    t21 = new URLSearchParams(buildMarketsUrlParams(overhaul, outcomesBounds, regionEnabled, identityEnabled)).toString();
    $[38] = identityEnabled;
    $[39] = outcomesBounds;
    $[40] = overhaul;
    $[41] = regionEnabled;
    $[42] = t21;
  } else {
    t21 = $[42];
  }
  const overhaulUrlKey = t21;
  let t22;
  let t23;
  if ($[43] !== debouncedSearch || $[44] !== overhaulUrlKey || $[45] !== pathname || $[46] !== searchParams || $[47] !== sortBy || $[48] !== status) {
    t22 = () => {
      const qs_0 = buildSearchString(sortBy, status, debouncedSearch, searchParams, overhaulUrlKey);
      const target = `${pathname}${qs_0}`;
      const current = `${pathname}${searchParams.toString().length > 0 ? `?${searchParams.toString()}` : ""}`;
      if (target !== current) {
        window.history.replaceState(null, "", target);
      }
    };
    t23 = [sortBy, status, debouncedSearch, pathname, searchParams, overhaulUrlKey];
    $[43] = debouncedSearch;
    $[44] = overhaulUrlKey;
    $[45] = pathname;
    $[46] = searchParams;
    $[47] = sortBy;
    $[48] = status;
    $[49] = t22;
    $[50] = t23;
  } else {
    t22 = $[49];
    t23 = $[50];
  }
  useEffect(t22, t23);
  let t24;
  if ($[51] !== status) {
    t24 = resolveStatusArg(status);
    $[51] = status;
    $[52] = t24;
  } else {
    t24 = $[52];
  }
  const statusArg = t24;
  const effectiveSortBy = status === "resolved" ? "resolvedAt" : sortBy;
  let t25;
  if ($[53] !== marketsWatchlist?.pubkeys || $[54] !== status || $[55] !== winnersWatchlist?.pubkeys) {
    t25 = status === "resolved" ? Array.from(new Set([...(marketsWatchlist?.pubkeys ?? []), ...(winnersWatchlist?.pubkeys ?? [])])) : marketsWatchlist?.pubkeys ?? [];
    $[53] = marketsWatchlist?.pubkeys;
    $[54] = status;
    $[55] = winnersWatchlist?.pubkeys;
    $[56] = t25;
  } else {
    t25 = $[56];
  }
  const watchlistPubkeys = t25;
  let t26;
  if ($[57] !== watchlistActive || $[58] !== watchlistPubkeys) {
    t26 = watchlistActive && watchlistPubkeys.length === 0 ? {
      pubkeys: WATCHLIST_EMPTY_SENTINEL
    } : watchlistActive ? {
      pubkeys: watchlistPubkeys.join(",")
    } : {};
    $[57] = watchlistActive;
    $[58] = watchlistPubkeys;
    $[59] = t26;
  } else {
    t26 = $[59];
  }
  const watchlistFilter = t26;
  const [nowMs] = useState(_temp8);
  let t27;
  if ($[60] !== identityEnabled || $[61] !== overhaul.marketType) {
    t27 = marketTypeFilterParams(overhaul.marketType, identityEnabled);
    $[60] = identityEnabled;
    $[61] = overhaul.marketType;
    $[62] = t27;
  } else {
    t27 = $[62];
  }
  let t28;
  if ($[63] !== overhaul.location || $[64] !== regionEnabled) {
    t28 = regionEnabled ? csvParam("location", asOptArr(overhaul.location).map(_temp9)) : {};
    $[63] = overhaul.location;
    $[64] = regionEnabled;
    $[65] = t28;
  } else {
    t28 = $[65];
  }
  let t29;
  if ($[66] !== outcomesBounds.max || $[67] !== outcomesBounds.min || $[68] !== overhaul.outcomes) {
    t29 = rangeParams(asRangeVal(overhaul.outcomes), outcomesBounds.min, outcomesBounds.max, "outcomesMin", "outcomesMax", intString);
    $[66] = outcomesBounds.max;
    $[67] = outcomesBounds.min;
    $[68] = overhaul.outcomes;
    $[69] = t29;
  } else {
    t29 = $[69];
  }
  let t30;
  if ($[70] !== overhaul.volume) {
    t30 = rangeParams(asRangeVal(overhaul.volume), M.volume.min, M.volume.max, "volumeMin", "volumeMax", dollarsToD6);
    $[70] = overhaul.volume;
    $[71] = t30;
  } else {
    t30 = $[71];
  }
  let t31;
  if ($[72] !== overhaul.prizePool) {
    t31 = rangeParams(asRangeVal(overhaul.prizePool), M.prizePool.min, M.prizePool.max, "prizePoolMin", "prizePoolMax", dollarsToD6);
    $[72] = overhaul.prizePool;
    $[73] = t31;
  } else {
    t31 = $[73];
  }
  let t32;
  if ($[74] !== overhaul.liquidity) {
    t32 = rangeParams(asRangeVal(overhaul.liquidity), M.liquidity.min, M.liquidity.max, "liquidityMin", "liquidityMax", dollarsToD6);
    $[74] = overhaul.liquidity;
    $[75] = t32;
  } else {
    t32 = $[75];
  }
  let t33;
  if ($[76] !== overhaul.backers) {
    t33 = rangeParams(asRangeVal(overhaul.backers), M.backers.min, M.backers.max, "backersMin", "backersMax", intString);
    $[76] = overhaul.backers;
    $[77] = t33;
  } else {
    t33 = $[77];
  }
  let t34;
  if ($[78] !== overhaul.traders) {
    t34 = rangeParams(asRangeVal(overhaul.traders), M.traders.min, M.traders.max, "tradersMin", "tradersMax", intString);
    $[78] = overhaul.traders;
    $[79] = t34;
  } else {
    t34 = $[79];
  }
  let t35;
  if ($[80] !== nowMs || $[81] !== overhaul.createdAt) {
    t35 = dateRangeParams(asRangeVal(overhaul.createdAt), MARKETS_CREATED_SPAN_DAYS, nowMs, "createdAfter", "createdBefore");
    $[80] = nowMs;
    $[81] = overhaul.createdAt;
    $[82] = t35;
  } else {
    t35 = $[82];
  }
  let t36;
  if ($[83] !== overhaul.creator) {
    t36 = creatorParam("creator", asOptArr(overhaul.creator));
    $[83] = overhaul.creator;
    $[84] = t36;
  } else {
    t36 = $[84];
  }
  let t37;
  if ($[85] !== t27 || $[86] !== t28 || $[87] !== t29 || $[88] !== t30 || $[89] !== t31 || $[90] !== t32 || $[91] !== t33 || $[92] !== t34 || $[93] !== t35 || $[94] !== t36) {
    t37 = {
      ...t27,
      ...t28,
      ...t29,
      ...t30,
      ...t31,
      ...t32,
      ...t33,
      ...t34,
      ...t35,
      ...t36
    };
    $[85] = t27;
    $[86] = t28;
    $[87] = t29;
    $[88] = t30;
    $[89] = t31;
    $[90] = t32;
    $[91] = t33;
    $[92] = t34;
    $[93] = t35;
    $[94] = t36;
    $[95] = t37;
  } else {
    t37 = $[95];
  }
  const overhaulArgs = t37;
  let t38;
  if ($[96] !== effectiveSortBy) {
    t38 = getMarketsFeedSortOrder(effectiveSortBy);
    $[96] = effectiveSortBy;
    $[97] = t38;
  } else {
    t38 = $[97];
  }
  const t39 = true;
  let t40;
  if ($[98] !== statusArg) {
    t40 = statusArg !== undefined ? {
      status: statusArg
    } : {};
    $[98] = statusArg;
    $[99] = t40;
  } else {
    t40 = $[99];
  }
  const t41 = categorySelected.length > 0 ? {
    category: categorySelected.map(categoryNameToId).join(",")
  } : {};
  let t42;
  if ($[100] !== debouncedSearch) {
    t42 = debouncedSearch.length > 0 ? {
      search: debouncedSearch
    } : {};
    $[100] = debouncedSearch;
    $[101] = t42;
  } else {
    t42 = $[101];
  }
  let t43;
  if ($[102] !== effectiveSortBy || $[103] !== overhaulArgs || $[104] !== t38 || $[105] !== t40 || $[106] !== t41 || $[107] !== t42 || $[108] !== watchlistFilter) {
    t43 = {
      sortBy: effectiveSortBy,
      sortOrder: t38,
      viewportGated: t39,
      ...t40,
      ...t41,
      ...t42,
      ...overhaulArgs,
      ...watchlistFilter
    };
    $[102] = effectiveSortBy;
    $[103] = overhaulArgs;
    $[104] = t38;
    $[105] = t40;
    $[106] = t41;
    $[107] = t42;
    $[108] = watchlistFilter;
    $[109] = t43;
  } else {
    t43 = $[109];
  }
  let t44;
  if ($[110] !== effectiveSortBy) {
    t44 = effectiveSortBy === "endingSoon" ? {
      refetchInterval: 5000
    } : undefined;
    $[110] = effectiveSortBy;
    $[111] = t44;
  } else {
    t44 = $[111];
  }
  const feedQuery = useGetMarketsInfinite(t43, t44);
  const {
    Items,
    ids,
    isPending,
    isError,
    isPlaceholderData,
    hasNextPage,
    isFetchingNextPage,
    refetch
  } = feedQuery;
  const totalMarkets = ids.length;
  let t45;
  if ($[112] !== ids || $[113] !== isError || $[114] !== isPending || $[115] !== isPlaceholderData) {
    t45 = resolveMarketsFeedPhase({
      isPending,
      isPlaceholderData,
      isError
    }, ids);
    $[112] = ids;
    $[113] = isError;
    $[114] = isPending;
    $[115] = isPlaceholderData;
    $[116] = t45;
  } else {
    t45 = $[116];
  }
  const feedPhase = t45;
  let t46;
  if ($[117] !== refetch) {
    t46 = () => {
      refetch();
    };
    $[117] = refetch;
    $[118] = t46;
  } else {
    t46 = $[118];
  }
  const retryFeed = t46;
  const showCreateCta = totalMarkets >= 1 && !hasNextPage;
  let marketRowIndexById;
  if ($[119] !== ids) {
    marketRowIndexById = new Map();
    for (const id of ids) {
      if (!marketRowIndexById.has(id)) {
        marketRowIndexById.set(id, marketRowIndexById.size);
      }
    }
    $[119] = ids;
    $[120] = marketRowIndexById;
  } else {
    marketRowIndexById = $[120];
  }
  const showFeaturedHero = categorySelected.length === 0;
  let t47;
  if ($[121] === Symbol.for("react.memo_cache_sentinel")) {
    t47 = [];
    $[121] = t47;
  } else {
    t47 = $[121];
  }
  useEffect(_temp10, t47);
  const setActiveMarketsSort = useMarketsSortStore(_temp11);
  let t48;
  let t49;
  if ($[122] !== setActiveMarketsSort || $[123] !== sortBy) {
    t48 = () => {
      setActiveMarketsSort(sortBy);
    };
    t49 = [sortBy, setActiveMarketsSort];
    $[122] = setActiveMarketsSort;
    $[123] = sortBy;
    $[124] = t48;
    $[125] = t49;
  } else {
    t48 = $[124];
    t49 = $[125];
  }
  useEffect(t48, t49);
  let t50;
  if ($[126] !== applyCategoryToUrl || $[127] !== categorySelected || $[128] !== overhaul) {
    t50 = function handleFilterChange(key_0, value_0) {
      if (key_0 === "category") {
        const cats = asStrArr(value_0);
        onEvent("markets_filter_changed", {
          filterType: "category",
          value: cats.length > 0 ? cats.join(",") : "all",
          previousValue: categorySelected.length > 0 ? categorySelected.join(",") : "all"
        });
        applyCategoryToUrl(cats);
        return;
      }
      setOverhaulValue(key_0, value_0);
      const filterType = MARKETS_FILTER_TYPE_BY_KEY[key_0];
      if (filterType !== undefined) {
        onEvent("markets_filter_changed", {
          filterType,
          value: summariseFilterValue(value_0),
          previousValue: summariseFilterValue(overhaul[key_0])
        });
      }
    };
    $[126] = applyCategoryToUrl;
    $[127] = categorySelected;
    $[128] = overhaul;
    $[129] = t50;
  } else {
    t50 = $[129];
  }
  const handleFilterChange = t50;
  let t51;
  if ($[130] !== applyCategoryToUrl || $[131] !== outcomesBounds || $[132] !== setStatus) {
    t51 = function handleClearFilters() {
      setOverhaul(buildEmptyMarketsOverhaul(outcomesBounds));
      applyCategoryToUrl([]);
      setStatus(DEFAULT_STATUS);
    };
    $[130] = applyCategoryToUrl;
    $[131] = outcomesBounds;
    $[132] = setStatus;
    $[133] = t51;
  } else {
    t51 = $[133];
  }
  const handleClearFilters = t51;
  let t52;
  if ($[134] !== setWatchlistActive || $[135] !== watchlistActive) {
    t52 = function handleWatchlistToggle() {
      const next = !watchlistActive;
      setWatchlistActive(next);
      onEvent("markets_watchlist_toggled", {
        active: next
      });
    };
    $[134] = setWatchlistActive;
    $[135] = watchlistActive;
    $[136] = t52;
  } else {
    t52 = $[136];
  }
  const handleWatchlistToggle = t52;
  let t53;
  if ($[137] !== ensureAuthenticated || $[138] !== handleWatchlistToggle) {
    t53 = function handleWatchlistClick() {
      if (!ensureAuthenticated()) {
        return;
      }
      handleWatchlistToggle();
    };
    $[137] = ensureAuthenticated;
    $[138] = handleWatchlistToggle;
    $[139] = t53;
  } else {
    t53 = $[139];
  }
  const handleWatchlistClick = t53;
  let t54;
  if ($[140] !== overhaulArgs) {
    t54 = Object.keys(overhaulArgs);
    $[140] = overhaulArgs;
    $[141] = t54;
  } else {
    t54 = $[141];
  }
  const overhaulActive = t54.length > 0;
  const explicitFilterActive = overhaulActive || categorySelected.length > 0 || debouncedSearch.length > 0 || watchlistActive || status !== DEFAULT_STATUS && status !== "resolved";
  const t55 = status === "resolved";
  const t56 = sortBy === "endingSoon";
  let t57;
  if ($[142] !== explicitFilterActive || $[143] !== t55 || $[144] !== t56) {
    t57 = resolveMarketsEmptyVariant({
      explicitFilterActive,
      isResolvedView: t55,
      isEndingSoonSort: t56
    });
    $[142] = explicitFilterActive;
    $[143] = t55;
    $[144] = t56;
    $[145] = t57;
  } else {
    t57 = $[145];
  }
  const emptyStateVariant = t57;
  const listIsLoading = feedPhase === "loading" || watchlistLoading;
  let t58;
  if ($[146] !== t) {
    t58 = t("sort.trending");
    $[146] = t;
    $[147] = t58;
  } else {
    t58 = $[147];
  }
  let t59;
  if ($[148] !== t58) {
    t59 = {
      label: t58,
      value: "trending",
      prefix: FireIconSolid
    };
    $[148] = t58;
    $[149] = t59;
  } else {
    t59 = $[149];
  }
  let t60;
  if ($[150] !== t) {
    t60 = t("sort.newest");
    $[150] = t;
    $[151] = t60;
  } else {
    t60 = $[151];
  }
  let t61;
  if ($[152] !== t60) {
    t61 = {
      label: t60,
      value: "tradingStartedAt",
      prefix: Plant04IconSolid
    };
    $[152] = t60;
    $[153] = t61;
  } else {
    t61 = $[153];
  }
  let t62;
  if ($[154] !== t) {
    t62 = t("sort.biggestPrize");
    $[154] = t;
    $[155] = t62;
  } else {
    t62 = $[155];
  }
  let t63;
  if ($[156] !== t62) {
    t63 = {
      label: t62,
      value: "prizePool",
      prefix: Award01IconSolid
    };
    $[156] = t62;
    $[157] = t63;
  } else {
    t63 = $[157];
  }
  let t64;
  if ($[158] !== t) {
    t64 = t("sort.endingSoon");
    $[158] = t;
    $[159] = t64;
  } else {
    t64 = $[159];
  }
  let t65;
  if ($[160] !== t64) {
    t65 = {
      label: t64,
      value: "endingSoon",
      prefix: Clock04IconSolid
    };
    $[160] = t64;
    $[161] = t65;
  } else {
    t65 = $[161];
  }
  let t66;
  if ($[162] !== t) {
    t66 = t("sort.resolved");
    $[162] = t;
    $[163] = t66;
  } else {
    t66 = $[163];
  }
  let t67;
  if ($[164] !== t66) {
    t67 = {
      label: t66,
      value: RESOLVED_VIEW_VALUE,
      prefix: CheckmarkCircle02IconSolid
    };
    $[164] = t66;
    $[165] = t67;
  } else {
    t67 = $[165];
  }
  let t68;
  if ($[166] !== t59 || $[167] !== t61 || $[168] !== t63 || $[169] !== t65 || $[170] !== t67) {
    t68 = [t59, t61, t63, t65, t67];
    $[166] = t59;
    $[167] = t61;
    $[168] = t63;
    $[169] = t65;
    $[170] = t67;
    $[171] = t68;
  } else {
    t68 = $[171];
  }
  const sortOptions = t68;
  const activeSortPill = status === "resolved" ? RESOLVED_VIEW_VALUE : sortBy;
  let t69;
  if ($[172] !== setSortBy || $[173] !== setStatus || $[174] !== status) {
    t69 = function handleSortPillChange(next_0) {
      if (next_0 === RESOLVED_VIEW_VALUE) {
        setStatus("resolved");
        return;
      }
      if (status !== DEFAULT_STATUS) {
        setStatus(DEFAULT_STATUS);
      }
      setSortBy(next_0);
    };
    $[172] = setSortBy;
    $[173] = setStatus;
    $[174] = status;
    $[175] = t69;
  } else {
    t69 = $[175];
  }
  const handleSortPillChange = t69;
  let t70;
  if ($[176] !== status) {
    t70 = function handleSortPillEvent(value_1, previous) {
      if (value_1 === RESOLVED_VIEW_VALUE) {
        onEvent("markets_filter_changed", {
          filterType: "status",
          value: "resolved",
          previousValue: status
        });
        return;
      }
      onEvent("markets_sort_changed", {
        sort: value_1,
        previousSort: previous
      });
    };
    $[176] = status;
    $[177] = t70;
  } else {
    t70 = $[177];
  }
  const handleSortPillEvent = t70;
  let t71;
  if ($[178] !== tFilters) {
    t71 = tFilters("any");
    $[178] = tFilters;
    $[179] = t71;
  } else {
    t71 = $[179];
  }
  let t72;
  if ($[180] !== tFilters) {
    t72 = tFilters("after");
    $[180] = tFilters;
    $[181] = t72;
  } else {
    t72 = $[181];
  }
  let t73;
  if ($[182] !== tFilters) {
    t73 = tFilters("before");
    $[182] = tFilters;
    $[183] = t73;
  } else {
    t73 = $[183];
  }
  let t74;
  if ($[184] !== t71 || $[185] !== t72 || $[186] !== t73) {
    t74 = {
      any: t71,
      after: t72,
      before: t73
    };
    $[184] = t71;
    $[185] = t72;
    $[186] = t73;
    $[187] = t74;
  } else {
    t74 = $[187];
  }
  const dateLabels = t74;
  let t75;
  if ($[188] !== tFilters) {
    t75 = tFilters("any");
    $[188] = tFilters;
    $[189] = t75;
  } else {
    t75 = $[189];
  }
  const anyLabel = t75;
  let t76;
  if ($[190] !== regionEnabled || $[191] !== t) {
    t76 = regionEnabled ? [{
      type: "searchMulti" as const,
      key: "location",
      label: t("filter.region"),
      placeholder: t("filter.searchRegions"),
      options: REGION_MULTI_OPTIONS
    }] : [];
    $[190] = regionEnabled;
    $[191] = t;
    $[192] = t76;
  } else {
    t76 = $[192];
  }
  let t77;
  if ($[193] !== t) {
    t77 = t("filter.creator");
    $[193] = t;
    $[194] = t77;
  } else {
    t77 = $[194];
  }
  let t78;
  if ($[195] !== t) {
    t78 = t("filter.searchCreators");
    $[195] = t;
    $[196] = t78;
  } else {
    t78 = $[196];
  }
  let t79;
  if ($[197] !== t) {
    t79 = t("filter.noCreators");
    $[197] = t;
    $[198] = t79;
  } else {
    t79 = $[198];
  }
  let t80;
  if ($[199] !== t77 || $[200] !== t78 || $[201] !== t79) {
    t80 = {
      type: "creatorMulti",
      key: "creator",
      label: t77,
      placeholder: t78,
      emptyLabel: t79
    };
    $[199] = t77;
    $[200] = t78;
    $[201] = t79;
    $[202] = t80;
  } else {
    t80 = $[202];
  }
  let t81;
  if ($[203] !== t) {
    t81 = t("filter.category");
    $[203] = t;
    $[204] = t81;
  } else {
    t81 = $[204];
  }
  let t82;
  if ($[205] !== t81) {
    t82 = {
      type: "chipsMulti",
      key: "category",
      label: t81,
      options: CATEGORY_CHIP_OPTIONS
    };
    $[205] = t81;
    $[206] = t82;
  } else {
    t82 = $[206];
  }
  let t83;
  if ($[207] !== t) {
    t83 = t("filter.numberOfOutcomes");
    $[207] = t;
    $[208] = t83;
  } else {
    t83 = $[208];
  }
  const t84 = outcomesBounds.min;
  const t85 = outcomesBounds.max;
  let t86;
  if ($[209] !== t) {
    t86 = t("filter.outcomesUnit");
    $[209] = t;
    $[210] = t86;
  } else {
    t86 = $[210];
  }
  let t87;
  if ($[211] !== anyLabel || $[212] !== t86) {
    t87 = {
      format: "count",
      unit: t86,
      anyLabel
    };
    $[211] = anyLabel;
    $[212] = t86;
    $[213] = t87;
  } else {
    t87 = $[213];
  }
  let t88;
  if ($[214] !== outcomesBounds.max || $[215] !== outcomesBounds.min || $[216] !== t83 || $[217] !== t87) {
    t88 = {
      type: "slider",
      key: "outcomes",
      label: t83,
      min: t84,
      max: t85,
      step: 1,
      format: t87
    };
    $[214] = outcomesBounds.max;
    $[215] = outcomesBounds.min;
    $[216] = t83;
    $[217] = t87;
    $[218] = t88;
  } else {
    t88 = $[218];
  }
  let t89;
  if ($[219] !== t) {
    t89 = t("filter.totalVolume");
    $[219] = t;
    $[220] = t89;
  } else {
    t89 = $[220];
  }
  let t90;
  if ($[221] !== anyLabel || $[222] !== currencySymbol || $[223] !== exchangeRate) {
    t90 = {
      format: "money",
      anyLabel,
      rate: exchangeRate,
      symbol: currencySymbol
    };
    $[221] = anyLabel;
    $[222] = currencySymbol;
    $[223] = exchangeRate;
    $[224] = t90;
  } else {
    t90 = $[224];
  }
  let t91;
  if ($[225] !== t89 || $[226] !== t90) {
    t91 = {
      type: "slider",
      key: "volume",
      label: t89,
      min: M.volume.min,
      max: M.volume.max,
      format: t90
    };
    $[225] = t89;
    $[226] = t90;
    $[227] = t91;
  } else {
    t91 = $[227];
  }
  let t92;
  if ($[228] !== t) {
    t92 = t("filter.prizePool");
    $[228] = t;
    $[229] = t92;
  } else {
    t92 = $[229];
  }
  let t93;
  if ($[230] !== anyLabel || $[231] !== currencySymbol || $[232] !== exchangeRate) {
    t93 = {
      format: "money",
      anyLabel,
      rate: exchangeRate,
      symbol: currencySymbol
    };
    $[230] = anyLabel;
    $[231] = currencySymbol;
    $[232] = exchangeRate;
    $[233] = t93;
  } else {
    t93 = $[233];
  }
  let t94;
  if ($[234] !== t92 || $[235] !== t93) {
    t94 = {
      type: "slider",
      key: "prizePool",
      label: t92,
      min: M.prizePool.min,
      max: M.prizePool.max,
      format: t93
    };
    $[234] = t92;
    $[235] = t93;
    $[236] = t94;
  } else {
    t94 = $[236];
  }
  let t95;
  if ($[237] !== t) {
    t95 = t("filter.liquidity");
    $[237] = t;
    $[238] = t95;
  } else {
    t95 = $[238];
  }
  let t96;
  if ($[239] !== anyLabel || $[240] !== currencySymbol || $[241] !== exchangeRate) {
    t96 = {
      format: "money",
      anyLabel,
      rate: exchangeRate,
      symbol: currencySymbol
    };
    $[239] = anyLabel;
    $[240] = currencySymbol;
    $[241] = exchangeRate;
    $[242] = t96;
  } else {
    t96 = $[242];
  }
  let t97;
  if ($[243] !== t95 || $[244] !== t96) {
    t97 = {
      type: "slider",
      key: "liquidity",
      label: t95,
      min: M.liquidity.min,
      max: M.liquidity.max,
      format: t96
    };
    $[243] = t95;
    $[244] = t96;
    $[245] = t97;
  } else {
    t97 = $[245];
  }
  let t98;
  if ($[246] !== t) {
    t98 = t("filter.backers");
    $[246] = t;
    $[247] = t98;
  } else {
    t98 = $[247];
  }
  let t99;
  if ($[248] !== anyLabel) {
    t99 = {
      format: "int",
      anyLabel
    };
    $[248] = anyLabel;
    $[249] = t99;
  } else {
    t99 = $[249];
  }
  let t100;
  if ($[250] !== t98 || $[251] !== t99) {
    t100 = {
      type: "slider",
      key: "backers",
      label: t98,
      min: M.backers.min,
      max: M.backers.max,
      format: t99
    };
    $[250] = t98;
    $[251] = t99;
    $[252] = t100;
  } else {
    t100 = $[252];
  }
  let t101;
  if ($[253] !== t) {
    t101 = t("filter.traders");
    $[253] = t;
    $[254] = t101;
  } else {
    t101 = $[254];
  }
  let t102;
  if ($[255] !== anyLabel) {
    t102 = {
      format: "int",
      anyLabel
    };
    $[255] = anyLabel;
    $[256] = t102;
  } else {
    t102 = $[256];
  }
  let t103;
  if ($[257] !== t101 || $[258] !== t102) {
    t103 = {
      type: "slider",
      key: "traders",
      label: t101,
      min: M.traders.min,
      max: M.traders.max,
      format: t102
    };
    $[257] = t101;
    $[258] = t102;
    $[259] = t103;
  } else {
    t103 = $[259];
  }
  let t104;
  if ($[260] !== t) {
    t104 = t("filter.createdAt");
    $[260] = t;
    $[261] = t104;
  } else {
    t104 = $[261];
  }
  let t105;
  if ($[262] !== dateLabels || $[263] !== t104) {
    t105 = {
      type: "dateSlider",
      key: "createdAt",
      label: t104,
      spanDays: MARKETS_CREATED_SPAN_DAYS,
      labels: dateLabels
    };
    $[262] = dateLabels;
    $[263] = t104;
    $[264] = t105;
  } else {
    t105 = $[264];
  }
  let t106;
  if ($[265] !== marketTypeFilter || $[266] !== t100 || $[267] !== t103 || $[268] !== t105 || $[269] !== t76 || $[270] !== t80 || $[271] !== t82 || $[272] !== t88 || $[273] !== t91 || $[274] !== t94 || $[275] !== t97) {
    t106 = [...marketTypeFilter, ...t76, t80, t82, t88, t91, t94, t97, t100, t103, t105];
    $[265] = marketTypeFilter;
    $[266] = t100;
    $[267] = t103;
    $[268] = t105;
    $[269] = t76;
    $[270] = t80;
    $[271] = t82;
    $[272] = t88;
    $[273] = t91;
    $[274] = t94;
    $[275] = t97;
    $[276] = t106;
  } else {
    t106 = $[276];
  }
  const filterDimensions = t106;
  let t107;
  if ($[277] !== regionEnabled) {
    t107 = regionEnabled ? {} : {
      location: []
    };
    $[277] = regionEnabled;
    $[278] = t107;
  } else {
    t107 = $[278];
  }
  let t108;
  if ($[279] !== categorySelected || $[280] !== overhaul || $[281] !== t107) {
    t108 = {
      ...overhaul,
      ...t107,
      category: categorySelected
    };
    $[279] = categorySelected;
    $[280] = overhaul;
    $[281] = t107;
    $[282] = t108;
  } else {
    t108 = $[282];
  }
  const activeFilterValues = t108;
  let t109;
  if ($[283] !== setSearchOpen) {
    t109 = () => setSearchOpen(true);
    $[283] = setSearchOpen;
    $[284] = t109;
  } else {
    t109 = $[284];
  }
  let t110;
  if ($[285] !== t) {
    t110 = t("searchPlaceholder");
    $[285] = t;
    $[286] = t110;
  } else {
    t110 = $[286];
  }
  const t111 = searchModalOpen && "bg-background-tertiary";
  let t112;
  if ($[287] !== t111) {
    t112 = cn("text-foreground-tertiary hover:bg-background-tertiary", t111);
    $[287] = t111;
    $[288] = t112;
  } else {
    t112 = $[288];
  }
  let t113;
  if ($[289] !== t109 || $[290] !== t110 || $[291] !== t112) {
    t113 = <AnimatedButton variant="tertiary" size="default" prefix={Search02Icon} onClick={t109} aria-label={t110} className={t112} />;
    $[289] = t109;
    $[290] = t110;
    $[291] = t112;
    $[292] = t113;
  } else {
    t113 = $[292];
  }
  let t114;
  if ($[293] === Symbol.for("react.memo_cache_sentinel")) {
    t114 = <GlobeButton mode="market" />;
    $[293] = t114;
  } else {
    t114 = $[293];
  }
  let t115;
  if ($[294] !== setFilterOpen) {
    t115 = () => setFilterOpen(true);
    $[294] = setFilterOpen;
    $[295] = t115;
  } else {
    t115 = $[295];
  }
  let t116;
  if ($[296] !== t) {
    t116 = t("filter.all");
    $[296] = t;
    $[297] = t116;
  } else {
    t116 = $[297];
  }
  const t117 = filterOpen && "bg-background-tertiary";
  let t118;
  if ($[298] !== t117) {
    t118 = cn("text-foreground-tertiary hover:bg-background-tertiary", t117);
    $[298] = t117;
    $[299] = t118;
  } else {
    t118 = $[299];
  }
  let t119;
  if ($[300] !== t115 || $[301] !== t116 || $[302] !== t118) {
    t119 = <AnimatedButton variant="tertiary" size="default" prefix={FilterVerticalIcon} onClick={t115} aria-label={t116} className={t118} />;
    $[300] = t115;
    $[301] = t116;
    $[302] = t118;
    $[303] = t119;
  } else {
    t119 = $[303];
  }
  let t120;
  if ($[304] !== setHelpModalOpen) {
    t120 = () => setHelpModalOpen(true);
    $[304] = setHelpModalOpen;
    $[305] = t120;
  } else {
    t120 = $[305];
  }
  let t121;
  if ($[306] !== t) {
    t121 = t("helpAriaLabel");
    $[306] = t;
    $[307] = t121;
  } else {
    t121 = $[307];
  }
  const t122 = helpModalOpen && "bg-background-tertiary";
  let t123;
  if ($[308] !== t122) {
    t123 = cn("text-foreground-tertiary hover:bg-background-tertiary", t122);
    $[308] = t122;
    $[309] = t123;
  } else {
    t123 = $[309];
  }
  let t124;
  if ($[310] !== t120 || $[311] !== t121 || $[312] !== t123) {
    t124 = <AnimatedButton variant="tertiary" size="default" prefix={HelpCircleIcon} onClick={t120} aria-label={t121} className={t123} />;
    $[310] = t120;
    $[311] = t121;
    $[312] = t123;
    $[313] = t124;
  } else {
    t124 = $[313];
  }
  const t125 = watchlistActive ? Bookmark02IconSolid : Bookmark02Icon;
  let t126;
  if ($[314] !== t) {
    t126 = t("watchlist");
    $[314] = t;
    $[315] = t126;
  } else {
    t126 = $[315];
  }
  const t127 = watchlistActive && "bg-background-tertiary";
  let t128;
  if ($[316] !== t127) {
    t128 = cn("text-foreground-tertiary hover:bg-background-tertiary", t127);
    $[316] = t127;
    $[317] = t128;
  } else {
    t128 = $[317];
  }
  let t129;
  if ($[318] !== handleWatchlistClick || $[319] !== t125 || $[320] !== t126 || $[321] !== t128 || $[322] !== watchlistActive) {
    t129 = <AnimatedButton variant="tertiary" size="default" prefix={t125} onClick={handleWatchlistClick} aria-label={t126} aria-pressed={watchlistActive} className={t128} />;
    $[318] = handleWatchlistClick;
    $[319] = t125;
    $[320] = t126;
    $[321] = t128;
    $[322] = watchlistActive;
    $[323] = t129;
  } else {
    t129 = $[323];
  }
  let t130;
  if ($[324] !== t113 || $[325] !== t119 || $[326] !== t124 || $[327] !== t129) {
    t130 = <div className="flex shrink-0 items-center gap-0">{t113}{t114}{t119}{t124}{t129}</div>;
    $[324] = t113;
    $[325] = t119;
    $[326] = t124;
    $[327] = t129;
    $[328] = t130;
  } else {
    t130 = $[328];
  }
  const headerActions = t130;
  let t131;
  if ($[329] !== marketsWatchlist?.pubkeys) {
    t131 = marketsWatchlist?.pubkeys ?? [];
    $[329] = marketsWatchlist?.pubkeys;
    $[330] = t131;
  } else {
    t131 = $[330];
  }
  let t132;
  if ($[331] !== Items || $[332] !== activeFilterValues || $[333] !== activeSortPill || $[334] !== category || $[335] !== emptyStateVariant || $[336] !== feedPhase || $[337] !== filterDimensions || $[338] !== filterOpen || $[339] !== handleClearFilters || $[340] !== handleFilterChange || $[341] !== handleSortPillChange || $[342] !== handleSortPillEvent || $[343] !== hasNextPage || $[344] !== headerActions || $[345] !== helpModalOpen || $[346] !== ids || $[347] !== isFetchingNextPage || $[348] !== listIsLoading || $[349] !== marketRowIndexById || $[350] !== openGraduation || $[351] !== openManipulation || $[352] !== retryFeed || $[353] !== setFilterOpen || $[354] !== setHelpModalOpen || $[355] !== showCreateCta || $[356] !== showFeaturedHero || $[357] !== sortBy || $[358] !== sortOptions || $[359] !== status || $[360] !== t || $[361] !== t131 || $[362] !== tFilters || $[363] !== totalMarkets) {
    t132 = {
      t,
      tFilters,
      category,
      status,
      sortBy,
      showFeaturedHero,
      marketWatchlistPubkeys: t131,
      feedPhase,
      retryFeed,
      listIsLoading,
      totalMarkets,
      emptyStateVariant,
      showCreateCta,
      hasNextPage,
      isFetchingNextPage,
      ids,
      Items,
      marketRowIndexById,
      sortOptions,
      activeSortPill,
      handleSortPillChange,
      handleSortPillEvent,
      filterDimensions,
      activeFilterValues,
      handleFilterChange,
      handleClearFilters,
      filterOpen,
      setFilterOpen,
      helpModalOpen,
      setHelpModalOpen,
      openGraduation,
      openManipulation,
      headerActions
    };
    $[331] = Items;
    $[332] = activeFilterValues;
    $[333] = activeSortPill;
    $[334] = category;
    $[335] = emptyStateVariant;
    $[336] = feedPhase;
    $[337] = filterDimensions;
    $[338] = filterOpen;
    $[339] = handleClearFilters;
    $[340] = handleFilterChange;
    $[341] = handleSortPillChange;
    $[342] = handleSortPillEvent;
    $[343] = hasNextPage;
    $[344] = headerActions;
    $[345] = helpModalOpen;
    $[346] = ids;
    $[347] = isFetchingNextPage;
    $[348] = listIsLoading;
    $[349] = marketRowIndexById;
    $[350] = openGraduation;
    $[351] = openManipulation;
    $[352] = retryFeed;
    $[353] = setFilterOpen;
    $[354] = setHelpModalOpen;
    $[355] = showCreateCta;
    $[356] = showFeaturedHero;
    $[357] = sortBy;
    $[358] = sortOptions;
    $[359] = status;
    $[360] = t;
    $[361] = t131;
    $[362] = tFilters;
    $[363] = totalMarkets;
    $[364] = t132;
  } else {
    t132 = $[364];
  }
  return t132;
}
function _temp11(s_3) {
  return s_3.setActiveSort;
}
function _temp10() {
  onEvent("markets_page_viewed", {
    path: "/markets",
    locale: "en"
  });
}
function _temp9(o_0) {
  return locationNameToId(o_0.value);
}
function _temp8() {
  return Date.now();
}
function _temp7(s_2) {
  return s_2.setSearchOpen;
}
function _temp6(s_1) {
  return s_1.searchOpen;
}
function _temp5(s_0) {
  return s_0.setOpen;
}
function _temp4(s) {
  return s.setOpen;
}
function _temp3(c) {
  return c !== null;
}
function _temp2(config_0) {
  return config_0.maxOutcomes;
}
function _temp(config) {
  return config.minOutcomes;
}
export function MarketsPage() {
  const $ = _c(74);
  const {
    t,
    tFilters,
    category,
    status,
    sortBy,
    showFeaturedHero,
    marketWatchlistPubkeys,
    feedPhase,
    retryFeed,
    listIsLoading,
    totalMarkets,
    emptyStateVariant,
    showCreateCta,
    hasNextPage,
    isFetchingNextPage,
    ids,
    Items,
    marketRowIndexById,
    sortOptions,
    activeSortPill,
    handleSortPillChange,
    handleSortPillEvent,
    filterDimensions,
    activeFilterValues,
    handleFilterChange,
    handleClearFilters,
    filterOpen,
    setFilterOpen,
    helpModalOpen,
    setHelpModalOpen,
    openGraduation,
    openManipulation,
    headerActions
  } = useMarketsPage();
  const t0 = feedPhase !== "loading";
  let t1;
  if ($[0] !== t0) {
    t1 = {
      name: "markets",
      isReady: t0
    };
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  useRoutePerf(t1);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = cn("w-full", LISTING_INDEX_PAGE_STACK_CLASS);
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== marketWatchlistPubkeys || $[4] !== showFeaturedHero) {
    t3 = <MarketsFeaturedSection visible={showFeaturedHero} watchlistPubkeys={marketWatchlistPubkeys} />;
    $[3] = marketWatchlistPubkeys;
    $[4] = showFeaturedHero;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== category || $[7] !== headerActions) {
    t4 = <div id="all-markets" className="scroll-mt-35"><MarketsHeader category={category} actions={headerActions} /></div>;
    $[6] = category;
    $[7] = headerActions;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== tFilters) {
    t5 = tFilters("sheetTitleMarkets");
    $[9] = tFilters;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== tFilters) {
    t6 = tFilters("clearAll");
    $[11] = tFilters;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t) {
    t7 = t("filter.all");
    $[13] = t;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== tFilters) {
    t8 = tFilters("close");
    $[15] = tFilters;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== tFilters) {
    t9 = tFilters("reset");
    $[17] = tFilters;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== tFilters) {
    t10 = tFilters("showResults");
    $[19] = tFilters;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== tFilters) {
    t11 = count => tFilters("nSelected", {
      count
    });
    $[21] = tFilters;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== activeFilterValues || $[24] !== activeSortPill || $[25] !== filterDimensions || $[26] !== filterOpen || $[27] !== handleClearFilters || $[28] !== handleFilterChange || $[29] !== handleSortPillChange || $[30] !== handleSortPillEvent || $[31] !== setFilterOpen || $[32] !== sortOptions || $[33] !== t10 || $[34] !== t11 || $[35] !== t5 || $[36] !== t6 || $[37] !== t7 || $[38] !== t8 || $[39] !== t9) {
    t12 = <div className="mt-4 flex items-start gap-2 md:mt-6"><div className="flex-1 min-w-0"><SortFilterBar sortOptions={sortOptions} activeSort={activeSortPill} onSortChange={handleSortPillChange} filters={filterDimensions} activeFilters={activeFilterValues} onFilterChange={handleFilterChange} onClearAll={handleClearFilters} filterSheetTitle={t5} clearAllLabel={t6} filterButtonLabel={t7} closeLabel={t8} resetLabel={t9} applyLabel={t10} selectedCountLabel={t11} onSortEvent={handleSortPillEvent} filterOpen={filterOpen} onFilterOpenChange={setFilterOpen} hideInlineSettings={true} /></div></div>;
    $[23] = activeFilterValues;
    $[24] = activeSortPill;
    $[25] = filterDimensions;
    $[26] = filterOpen;
    $[27] = handleClearFilters;
    $[28] = handleFilterChange;
    $[29] = handleSortPillChange;
    $[30] = handleSortPillEvent;
    $[31] = setFilterOpen;
    $[32] = sortOptions;
    $[33] = t10;
    $[34] = t11;
    $[35] = t5;
    $[36] = t6;
    $[37] = t7;
    $[38] = t8;
    $[39] = t9;
    $[40] = t12;
  } else {
    t12 = $[40];
  }
  let t13;
  if ($[41] !== t12 || $[42] !== t4) {
    t13 = <div className="flex flex-col">{t4}{t12}</div>;
    $[41] = t12;
    $[42] = t4;
    $[43] = t13;
  } else {
    t13 = $[43];
  }
  let t14;
  if ($[44] !== Items || $[45] !== emptyStateVariant || $[46] !== feedPhase || $[47] !== hasNextPage || $[48] !== ids || $[49] !== isFetchingNextPage || $[50] !== listIsLoading || $[51] !== marketRowIndexById || $[52] !== retryFeed || $[53] !== showCreateCta || $[54] !== sortBy || $[55] !== status || $[56] !== totalMarkets) {
    t14 = feedPhase === "initialError" ? <MarketsFeedError phase="initial" onRetry={retryFeed} /> : listIsLoading ? <div data-testid="markets-grid-loading" className="grid grid-flow-row-dense grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">{SKELETON_STRIPE_KEYS.map(_temp12)}</div> : totalMarkets === 0 ? emptyStateVariant === "filtered" ? <NoResults /> : <EmptyState variant={emptyStateVariant} /> : <>{feedPhase === "staleError" ? <MarketsFeedError phase="stale" onRetry={retryFeed} /> : null}<MarketFeedTradeProvider marketIds={ids}><div data-testid="loaded-markets-grid" className="grid grid-flow-row-dense grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">{status === DEFAULT_STATUS && <IntroWidget active={sortBy === DEFAULT_SORT} />}<Items>{t15 => {
              const {
                ref,
                id
              } = t15;
              return <MarketRowCard ref={ref} pubkey={id} priorityImage={(marketRowIndexById.get(id) ?? 0) < 6} />;
            }}</Items>{showCreateCta && <SparseCard />}</div></MarketFeedTradeProvider>{hasNextPage && isFetchingNextPage && <div className="flex justify-center py-4"><Icon icon={Loading03Icon} className="size-6 animate-spin text-foreground-secondary" /></div>}</>;
    $[44] = Items;
    $[45] = emptyStateVariant;
    $[46] = feedPhase;
    $[47] = hasNextPage;
    $[48] = ids;
    $[49] = isFetchingNextPage;
    $[50] = listIsLoading;
    $[51] = marketRowIndexById;
    $[52] = retryFeed;
    $[53] = showCreateCta;
    $[54] = sortBy;
    $[55] = status;
    $[56] = totalMarkets;
    $[57] = t14;
  } else {
    t14 = $[57];
  }
  let t15;
  if ($[58] !== openGraduation || $[59] !== setHelpModalOpen) {
    t15 = () => {
      setHelpModalOpen(false);
      openGraduation(true);
    };
    $[58] = openGraduation;
    $[59] = setHelpModalOpen;
    $[60] = t15;
  } else {
    t15 = $[60];
  }
  let t16;
  if ($[61] !== openManipulation || $[62] !== setHelpModalOpen) {
    t16 = () => {
      setHelpModalOpen(false);
      openManipulation(true);
    };
    $[61] = openManipulation;
    $[62] = setHelpModalOpen;
    $[63] = t16;
  } else {
    t16 = $[63];
  }
  let t17;
  if ($[64] !== helpModalOpen || $[65] !== setHelpModalOpen || $[66] !== t15 || $[67] !== t16) {
    t17 = <MarketsHowItWorksModal open={helpModalOpen} onOpenChange={setHelpModalOpen} onWhatIsGraduation={t15} onPreventManipulation={t16} />;
    $[64] = helpModalOpen;
    $[65] = setHelpModalOpen;
    $[66] = t15;
    $[67] = t16;
    $[68] = t17;
  } else {
    t17 = $[68];
  }
  let t18;
  if ($[69] !== t13 || $[70] !== t14 || $[71] !== t17 || $[72] !== t3) {
    t18 = <div className={t2}>{t3}{t13}{t14}{t17}</div>;
    $[69] = t13;
    $[70] = t14;
    $[71] = t17;
    $[72] = t3;
    $[73] = t18;
  } else {
    t18 = $[73];
  }
  return t18;
}
function _temp12(key) {
  return <MarketRowCardSkeleton key={key} />;
}